/* TopShelf service pages: scroll-driven storytelling for sections/topshelf-svc-*.liquid.
   Writes progress to CSS custom properties; all visual motion lives in
   assets/topshelf-service-pages.css under html.tsv-motion. */
(() => {
  if (window.__topshelfSvcBound) return;
  window.__topshelfSvcBound = true;

  const root = document.documentElement;
  const reduceMq = matchMedia('(prefers-reduced-motion: reduce)');
  const fineMq = matchMedia('(hover: hover) and (pointer: fine)');
  const desktopMq = matchMedia('(min-width: 990px)');
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const motion = () => !reduceMq.matches;
  const clamp = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);

  root.classList.add('tsv-js');
  const applyMotion = () => root.classList.toggle('tsv-motion', motion());
  applyMotion();

  /* ---------- Scroll engine ---------- */

  const live = new Set();
  const progressFns = new Map();
  const frameHooks = new Set();
  const cache = new WeakMap();
  const bound = new WeakMap();
  let queued = false;
  let vh = window.innerHeight;

  const setVar = (el, name, value) => {
    let c = cache.get(el);
    if (!c) cache.set(el, (c = {}));
    if (c[name] !== undefined && Math.abs(c[name] - value) < 0.0005) return;
    c[name] = value;
    el.style.setProperty(name, value.toFixed(4));
  };

  const trackProgress = (el, r) => {
    const dist = r.height - vh;
    if (el.dataset.tsvTrack === 'pass' || dist <= 1) return clamp((vh - r.top) / (vh + r.height));
    return clamp(-r.top / dist);
  };

  const box = (el) =>
    el.classList.contains('tsv') && el.parentElement && el.parentElement.classList.contains('shopify-section')
      ? el.parentElement
      : el;

  const apply = (el, r) => {
    if (el.classList.contains('tsv')) {
      setVar(el, '--enter', clamp((vh - r.top) / (vh * 0.9)));
      setVar(el, '--exit', clamp((vh - r.bottom) / (vh * 0.9)));
      return;
    }
    const p = trackProgress(el, r);
    setVar(el, '--p', p);
    const fns = progressFns.get(el);
    if (fns) for (const fn of fns) fn(p);
  };

  const frame = () => {
    queued = false;
    vh = window.innerHeight;
    const reads = [];
    for (const el of live) reads.push([el, box(el).getBoundingClientRect()]);
    const hookReads = [];
    for (const hook of frameHooks) hookReads.push([hook, hook.read ? hook.read() : undefined]);
    for (const [el, r] of reads) apply(el, r);
    for (const [hook, data] of hookReads) hook.write && hook.write(data);
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(frame);
  };

  const liveIo = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) live.add(e.target);
        else {
          live.delete(e.target);
          vh = window.innerHeight;
          apply(e.target, box(e.target).getBoundingClientRect());
        }
      }
      schedule();
    },
    { rootMargin: '15% 0px' }
  );

  const onProgress = (el, fn) => {
    if (!progressFns.has(el)) progressFns.set(el, []);
    progressFns.get(el).push(fn);
  };

  /* ---------- Off-screen pause ----------
     Each animated section's .shopify-section wrapper gets .tsv-idle while it is well outside the
     viewport; section CSS uses it to set animation-play-state: paused on its ambient loops.
     SMIL (<animate*>) ignores CSS, so outermost SVGs that contain it are paused here directly. */

  const idleHosts = new Set();
  const smilCache = new WeakMap();
  const smilIn = (host) => {
    let list = smilCache.get(host);
    if (!list) {
      list = [...host.querySelectorAll('svg')].filter(
        (s) =>
          s.pauseAnimations &&
          !(s.parentElement && s.parentElement.closest('svg')) &&
          s.querySelector('animate, animateMotion, animateTransform, set')
      );
      smilCache.set(host, list);
    }
    return list;
  };

  const setIdle = (host, idle) => {
    host.classList.toggle('tsv-idle', idle);
    const freeze = idle || !motion();
    for (const svg of smilIn(host)) {
      if (freeze) svg.pauseAnimations();
      else svg.unpauseAnimations();
    }
  };

  const idleIo = new IntersectionObserver(
    (entries) => {
      for (const e of entries) setIdle(e.target, !e.isIntersecting);
    },
    { rootMargin: '20% 0px' }
  );

  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  reduceMq.addEventListener && reduceMq.addEventListener('change', () => {
    applyMotion();
    for (const host of idleHosts) setIdle(host, host.classList.contains('tsv-idle'));
    schedule();
  });

  /* ---------- Reveal on enter ---------- */

  const revealIo = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-in');
        revealIo.unobserve(e.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  /* ---------- Number formatting + count-ups ---------- */

  const fmt = (n, d, group = true) =>
    n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: group });

  const countIo = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        countIo.unobserve(e.target);
        const el = e.target;
        const to = parseFloat(el.dataset.to);
        const d = parseInt(el.dataset.decimals || '0', 10);
        const group = !('nogroup' in el.dataset);
        const t0 = performance.now();
        const dur = 1600;
        const tick = (t) => {
          const k = clamp((t - t0) / dur);
          const eased = k === 1 ? 1 : 1 - Math.pow(2, -10 * k);
          el.textContent = fmt(to * eased, d, group);
          if (k < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    },
    { threshold: 0.6 }
  );

  const initCountup = (el) => {
    if (!motion() || isNaN(parseFloat(el.dataset.to))) return;
    el.textContent = fmt(0, parseInt(el.dataset.decimals || '0', 10), false);
    countIo.observe(el);
  };

  const initTrackCount = (el) => {
    const track = el.closest('[data-tsv-track]');
    if (!track || !motion()) return;
    const from = parseFloat(el.dataset.from || '0');
    const to = parseFloat(el.dataset.to);
    const start = parseFloat(el.dataset.start || '0');
    const end = parseFloat(el.dataset.end || '0.6');
    const d = parseInt(el.dataset.decimals || '0', 10);
    let last = '';
    el.textContent = fmt(from, d);
    onProgress(track, (p) => {
      const k = clamp((p - start) / (end - start));
      const eased = 1 - Math.pow(1 - k, 3);
      const text = fmt(from + (to - from) * eased, d);
      if (text !== last) el.textContent = last = text;
    });
  };

  /* ---------- Video ---------- */

  const vidIo = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const v = e.target;
        if (e.isIntersecting) {
          if (v.dataset.tsvVideo === 'hero' && !v.dataset.tsvHeroReady) continue;
          play(v);
        } else v.pause();
      }
    },
    { rootMargin: '200px 0px' }
  );

  const play = (v) => {
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };

  const initVideo = (v) => {
    if (!motion() || saveData) return;
    v.addEventListener('playing', () => v.classList.add('is-playing'), { once: true });
    const mode = v.dataset.tsvVideo;
    if (mode === 'hero') {
      const start = () =>
        setTimeout(() => {
          v.dataset.tsvHeroReady = '1';
          v.preload = 'auto';
          play(v);
          vidIo.observe(v);
        }, 200);
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    } else if (mode === 'lazy') {
      vidIo.observe(v);
    } else if (mode === 'hover') {
      if (!fineMq.matches) return;
      const host = v.closest('[data-tsv-hover]') || v.parentElement;
      host.addEventListener('pointerenter', () => play(v));
      host.addEventListener('pointerleave', () => v.pause());
      host.addEventListener('focusin', () => play(v));
      host.addEventListener('focusout', () => v.pause());
    }
  };

  /* ---------- Step stories ---------- */

  const initSteps = (track) => {
    const section = track.closest('.tsv');
    const stage = track.querySelector('.tsv-stage') || track;
    const n = parseInt(track.dataset.tsvSteps, 10) || 1;
    const groups = [];
    for (let i = 0; i < n; i++) groups.push([...track.querySelectorAll(`[data-tsv-step="${i}"]`)]);
    let current = -1;
    const set = (i) => {
      if (i === current) return;
      current = i;
      groups.forEach((els, k) => {
        for (const el of els) {
          el.classList.toggle('is-active', k === i);
          el.classList.toggle('is-past', k < i);
          if (el.tagName === 'VIDEO' && el.dataset.tsvVideo === 'step' && motion() && !saveData) {
            if (k === i && el.offsetParent !== null) play(el);
            else el.pause();
          }
          if (k === i && el.dataset.tsvSceneBg) stage.style.setProperty('--tsv-scene-bg', el.dataset.tsvSceneBg);
        }
      });
      if (section) section.dataset.step = String(i);
    };
    for (const v of track.querySelectorAll('video[data-tsv-video="step"]')) {
      if (!motion() || saveData) continue;
      v.addEventListener('playing', () => v.classList.add('is-playing'), { once: true });
      v.preload = 'metadata';
    }
    set(0);
    onProgress(track, (p) => set(Math.min(n - 1, Math.floor(p * n * 0.9999))));
  };

  /* ---------- Canvas frame scrub ---------- */

  const initScrub = (canvas) => {
    if (!motion()) return;
    const track = canvas.closest('[data-tsv-track]');
    const count = parseInt(canvas.dataset.count, 10);
    if (!track || !count) return;
    const small = matchMedia('(max-width: 749px)').matches;
    const pattern = (small && canvas.dataset.srcSm) || canvas.dataset.src;
    const url = (i) => pattern.replace('{n}', String(i + 1).padStart(3, '0'));
    const ctx = canvas.getContext('2d');
    const imgs = new Array(count);
    const ok = new Array(count).fill(false);
    const order = [];
    const seen = new Set();
    for (const step of [12, 6, 3, 1]) {
      for (let i = 0; i < count; i += step) {
        if (!seen.has(i)) {
          seen.add(i);
          order.push(i);
        }
      }
    }
    if (!seen.has(count - 1)) order.splice(1, 0, count - 1);
    let p = 0;
    let drawn = -1;
    let w = 0;
    let h = 0;

    const nearest = (target) => {
      for (let d = 0; d < count; d++) {
        if (target - d >= 0 && ok[target - d]) return target - d;
        if (target + d < count && ok[target + d]) return target + d;
      }
      return -1;
    };

    const draw = (force) => {
      const i = nearest(Math.round(p * (count - 1)));
      if (i < 0 || (!force && i === drawn) || !w) return;
      drawn = i;
      const img = imgs[i];
      const s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
      const dw = img.naturalWidth * s;
      const dh = img.naturalHeight * s;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      canvas.classList.add('is-ready');
    };

    const size = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.round(canvas.clientWidth * dpr);
      h = Math.round(canvas.clientHeight * dpr);
      if (!w || !h) return;
      canvas.width = w;
      canvas.height = h;
      draw(true);
    };

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      let next = 0;
      const pump = () => {
        if (next >= order.length) return;
        const i = order[next++];
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => {
          imgs[i] = img;
          ok[i] = true;
          const target = Math.round(p * (count - 1));
          if (drawn < 0 || Math.abs(i - target) < Math.abs(drawn - target)) draw(true);
          pump();
        };
        img.onerror = pump;
        img.src = url(i);
      };
      for (let k = 0; k < 4; k++) pump();
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
          io.disconnect();
        }
      },
      { rootMargin: '150% 0px' }
    );
    io.observe(track);
    if ('ResizeObserver' in window) new ResizeObserver(size).observe(canvas);
    else window.addEventListener('resize', size);
    size();
    onProgress(track, (v) => {
      p = v;
      draw(false);
    });
  };

  /* ---------- Horizontal reel ---------- */

  const initReel = (track) => {
    const row = track.querySelector('.tsv-reel__row');
    const stage = track.querySelector('.tsv-stage');
    if (!row || !stage) return;
    const measure = () => {
      if (!motion() || !desktopMq.matches) {
        track.style.removeProperty('--len');
        return;
      }
      const shift = Math.max(0, row.scrollWidth - stage.clientWidth);
      track.style.setProperty('--shift', shift + 'px');
      track.style.setProperty('--len', String(Math.round(100 + (shift / window.innerHeight) * 100)));
      schedule();
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
    desktopMq.addEventListener && desktopMq.addEventListener('change', measure);
    window.addEventListener('load', measure, { once: true });
  };

  /* ---------- Stacking sheets ---------- */

  const initStack = (stack) => {
    const cards = [...stack.querySelectorAll('.tsv-stack__card')];
    if (cards.length < 2) return;
    let visible = false;
    new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      schedule();
    }).observe(stack);
    frameHooks.add({
      read: () =>
        visible && motion() ? cards.map((c) => ({ top: c.getBoundingClientRect().top, h: c.offsetHeight })) : null,
      write: (rects) => {
        if (!rects) return;
        for (let i = 0; i < cards.length; i++) {
          const next = rects[i + 1];
          const cov = next ? clamp(1 - (next.top - rects[i].top) / rects[i].h) : 0;
          setVar(cards[i], '--cov', cov);
        }
      },
    });
  };

  /* ---------- Local nav + mobile dock ---------- */

  const scroller = () => {
    const pw = document.querySelector('.page-wrapper');
    if (pw && pw.scrollHeight > pw.clientHeight + 1 && getComputedStyle(pw).overflowY !== 'visible') return pw;
    return document.scrollingElement || root;
  };

  const initNav = (navroot) => {
    const nav = navroot.querySelector('[data-tsv-nav]');
    const dock = navroot.querySelector('[data-tsv-dock]');
    const list = nav && nav.querySelector('[data-tsv-nav-links]');
    const pill = list && list.querySelector('.tsv-nav__pill');
    const hero = document.querySelector('.tsv-hero__track, .tsv-hero');
    const cta = document.querySelector('.tsv-cta');
    const header = document.getElementById('header-group');
    const links = new Map();

    if (list) {
      for (const s of document.querySelectorAll('.tsv[data-tsv-anchor]')) {
        const label = (s.dataset.tsvAnchor || '').trim();
        if (!label || !s.id) continue;
        const a = document.createElement('a');
        a.href = '#' + s.id;
        a.textContent = label;
        a.addEventListener('click', (ev) => {
          ev.preventDefault();
          s.scrollIntoView({ behavior: motion() ? 'smooth' : 'auto', block: 'start' });
          history.replaceState(null, '', '#' + s.id);
        });
        list.appendChild(a);
        links.set(s, a);
      }
    }

    const top = nav && nav.querySelector('[data-tsv-top]');
    if (top) {
      top.addEventListener('click', (ev) => {
        ev.preventDefault();
        scroller().scrollTo({ top: 0, behavior: motion() ? 'smooth' : 'auto' });
      });
    }

    let activeLink = null;
    const setActive = (a) => {
      if (a === activeLink) return;
      if (activeLink) activeLink.classList.remove('is-active');
      activeLink = a;
      list.classList.toggle('has-active', !!a);
      if (!a) return;
      a.classList.add('is-active');
      pill.style.setProperty('--x', a.offsetLeft + 'px');
      pill.style.setProperty('--w', a.offsetWidth + 'px');
      const l = a.offsetLeft - list.scrollLeft;
      if (l < 0 || l + a.offsetWidth > list.clientWidth) {
        list.scrollTo({ left: a.offsetLeft - 24, behavior: motion() ? 'smooth' : 'auto' });
      }
    };

    const anchored = [...links.keys()];

    let ctaVisible = false;
    if (cta) {
      new IntersectionObserver((entries) => {
        ctaVisible = entries[0].isIntersecting;
        schedule();
      }).observe(cta);
    }

    const syncHeader = () => {
      const active = header && header.getAttribute('data-kith-sticky-state') === 'active';
      navroot.style.setProperty('--tsv-nav-offset', active ? header.offsetHeight + 'px' : '0px');
    };
    if (header) {
      new MutationObserver(syncHeader).observe(header, { attributes: true, attributeFilter: ['data-kith-sticky-state'] });
      syncHeader();
    }

    frameHooks.add({
      read: () => {
        const sc = scroller();
        const max = sc.scrollHeight - sc.clientHeight;
        let current = null;
        if (nav && desktopMq.matches) {
          for (const s of anchored) {
            if (s.getBoundingClientRect().top < vh * 0.45) current = s;
            else break;
          }
        }
        return {
          heroBottom: hero ? hero.getBoundingClientRect().bottom : -1,
          progress: max > 0 ? sc.scrollTop / max : 0,
          current,
        };
      },
      write: ({ heroBottom, progress, current }) => {
        if (nav) {
          nav.classList.toggle('is-shown', heroBottom < 64);
          setVar(nav, '--tsv-progress', clamp(progress));
          if (desktopMq.matches && list) setActive(current ? links.get(current) : null);
        }
        if (dock) dock.classList.toggle('is-shown', heroBottom < vh * 0.35 && !ctaVisible);
      },
    });
  };

  /* ---------- Pointer: spotlight + magnetic buttons ---------- */

  let pointerQueued = false;
  let lastEv = null;
  let magnet = null;
  const resetMagnet = () => {
    if (!magnet) return;
    magnet.style.setProperty('--tx', '0px');
    magnet.style.setProperty('--ty', '0px');
    magnet = null;
  };
  const pointerFrame = () => {
    pointerQueued = false;
    const ev = lastEv;
    if (!ev || !(ev.target instanceof Element)) return;
    const spot = ev.target.closest('[data-tsv-spot]');
    if (spot) {
      const r = spot.getBoundingClientRect();
      spot.style.setProperty('--mx', ev.clientX - r.left + 'px');
      spot.style.setProperty('--my', ev.clientY - r.top + 'px');
    }
    const m = motion() ? ev.target.closest('[data-tsv-magnet]') : null;
    if (m !== magnet) resetMagnet();
    if (m) {
      magnet = m;
      const r = m.getBoundingClientRect();
      const dx = ev.clientX - (r.left + r.width / 2);
      const dy = ev.clientY - (r.top + r.height / 2);
      m.style.setProperty('--tx', clamp(dx * 0.2, -8, 8).toFixed(1) + 'px');
      m.style.setProperty('--ty', clamp(dy * 0.3, -6, 6).toFixed(1) + 'px');
    }
  };
  if (fineMq.matches) {
    document.addEventListener(
      'pointermove',
      (ev) => {
        lastEv = ev;
        if (!pointerQueued) {
          pointerQueued = true;
          requestAnimationFrame(pointerFrame);
        }
      },
      { passive: true }
    );
    document.addEventListener('pointerleave', resetMagnet);
  }

  /* ---------- Carousels ---------- */

  const initCarousel = (wrap) => {
    const track = wrap.querySelector('[data-tsv-carousel-track]');
    if (!track) return;
    const prev = wrap.querySelector('[data-tsv-prev]');
    const next = wrap.querySelector('[data-tsv-next]');
    let raf = 0;
    const sync = () => {
      raf = 0;
      const max = track.scrollWidth - track.clientWidth;
      const x = Math.abs(track.scrollLeft);
      const atStart = x <= 2;
      const atEnd = x >= max - 2;
      wrap.classList.toggle('is-static', max <= 2);
      wrap.classList.toggle('is-start', atStart);
      wrap.classList.toggle('is-end', atEnd);
      if (prev) prev.setAttribute('aria-disabled', String(atStart));
      if (next) next.setAttribute('aria-disabled', String(atEnd));
      setVar(wrap, '--cp', max > 2 ? clamp(x / max) : 0);
      setVar(wrap, '--cv', track.scrollWidth ? clamp(track.clientWidth / track.scrollWidth) : 1);
    };
    const queue = () => {
      if (!raf) raf = requestAnimationFrame(sync);
    };
    const page = (dir, btn) => {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      const cs = getComputedStyle(track);
      const gap = parseFloat(cs.columnGap) || 0;
      const item = track.firstElementChild;
      const step = item ? item.getBoundingClientRect().width + gap : track.clientWidth;
      const view = track.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      const n = Math.max(1, Math.floor((view + gap) / step + 0.01));
      track.scrollBy({ left: dir * n * step, behavior: motion() ? 'smooth' : 'auto' });
    };
    if (prev) prev.addEventListener('click', () => page(-1, prev));
    if (next) next.addEventListener('click', () => page(1, next));
    track.addEventListener('scroll', queue, { passive: true });
    track.addEventListener('transitionend', queue);
    if ('ResizeObserver' in window) new ResizeObserver(queue).observe(track);
    else window.addEventListener('resize', queue, { passive: true });
    sync();
  };

  /* ---------- Plan finder ---------- */

  const initFinder = (form) => {
    const section = form.closest('.tsv-plans');
    let plans = [];
    try {
      plans = JSON.parse(form.dataset.plans || '[]');
    } catch (e) {}
    const groups = [...form.querySelectorAll('fieldset')];
    const nameEl = form.querySelector('[data-tsv-finder-name]');
    const visitsEl = form.querySelector('[data-tsv-finder-visits]');
    const cta = form.querySelector('[data-tsv-finder-cta]');
    const ctaTpl = cta ? cta.dataset.template || cta.textContent : '';
    form.addEventListener('submit', (ev) => ev.preventDefault());
    form.addEventListener('change', () => {
      const vals = groups.map((g) => {
        const c = g.querySelector('input:checked');
        return c ? parseInt(c.value, 10) : null;
      });
      if (vals.some((v) => v === null)) return;
      const rec = Math.min(plans.length - 1, Math.max(...vals));
      const plan = plans[rec];
      if (!plan) return;
      section.dataset.rec = String(rec + 1);
      if (nameEl) nameEl.textContent = plan.name;
      if (visitsEl) visitsEl.textContent = plan.visits;
      if (cta) cta.textContent = ctaTpl.replace('[plan]', plan.name);
      form.classList.add('has-result');
    });
  };

  /* ---------- Service areas ---------- */

  const norm = (s) =>
    s
      .toLowerCase()
      .replace(/\(.*?\)/g, ' ')
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const initAreas = (section) => {
    for (const r of section.querySelectorAll('[data-tsv-region]')) {
      const on = () => (section.dataset.hl = r.dataset.tsvRegion);
      const off = () => delete section.dataset.hl;
      r.addEventListener('pointerenter', on);
      r.addEventListener('pointerleave', off);
      r.addEventListener('focusin', on);
      r.addEventListener('focusout', off);
    }
    const form = section.querySelector('[data-tsv-checker]');
    if (!form) return;
    const input = form.querySelector('input');
    const out = form.querySelector('[data-tsv-checker-out]');
    const yes = out.querySelector('[data-tsv-yes]');
    const no = out.querySelector('[data-tsv-no]');
    const cityEl = out.querySelectorAll('[data-tsv-city]');
    const towns = [...form.querySelectorAll('datalist option')].map((o) => ({ label: o.value, key: norm(o.value) }));
    const show = (match, typed) => {
      out.hidden = false;
      out.classList.remove('is-pop');
      void out.offsetWidth;
      out.classList.add('is-pop');
      yes.hidden = !match;
      no.hidden = !!match;
      cityEl.forEach((el) => (el.textContent = match ? match.label : typed));
    };
    const check = (strict) => {
      const q = norm(input.value);
      if (!q) {
        out.hidden = true;
        return;
      }
      let match = towns.find((t) => t.key === q);
      if (!match && !strict) match = towns.find((t) => t.key.startsWith(q) || q.startsWith(t.key));
      if (match || !strict) show(match, input.value.trim());
    };
    input.addEventListener('input', () => check(true));
    input.addEventListener('change', () => check(false));
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      check(false);
    });
  };

  /* ---------- Init ---------- */

  const once = (el, fn) => {
    let seen = bound.get(fn);
    if (!seen) {
      seen = new WeakSet();
      bound.set(fn, seen);
    }
    if (seen.has(el)) return;
    seen.add(el);
    fn(el);
  };

  const observeLive = (el) => liveIo.observe(el);
  const observeReveal = (el) => revealIo.observe(el);
  const observeIdle = (host) => {
    idleHosts.add(host);
    idleIo.observe(host);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('.tsv, [data-tsv-track]').forEach((el) => {
      once(el, observeLive);
      once(el.closest('.shopify-section') || el, observeIdle);
    });
    if (motion()) scope.querySelectorAll('[data-tsv-reveal]').forEach((el) => once(el, observeReveal));
    scope.querySelectorAll('[data-tsv-countup]').forEach((el) => once(el, initCountup));
    scope.querySelectorAll('[data-tsv-count]').forEach((el) => once(el, initTrackCount));
    scope.querySelectorAll('video[data-tsv-video]:not([data-tsv-video="step"])').forEach((el) => once(el, initVideo));
    scope.querySelectorAll('[data-tsv-steps]').forEach((el) => once(el, initSteps));
    scope.querySelectorAll('canvas[data-tsv-scrub]').forEach((el) => once(el, initScrub));
    scope.querySelectorAll('.tsv-reel [data-tsv-track]').forEach((el) => once(el, initReel));
    scope.querySelectorAll('[data-tsv-stack]').forEach((el) => once(el, initStack));
    scope.querySelectorAll('[data-tsv-carousel]').forEach((el) => once(el, initCarousel));
    scope.querySelectorAll('[data-tsv-finder]').forEach((el) => once(el, initFinder));
    scope.querySelectorAll('.tsv-areas').forEach((el) => once(el, initAreas));
    scope.querySelectorAll('.tsv-navroot').forEach((el) => once(el, initNav));
    schedule();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (ev) => init(ev.target));
})();
